import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTruckRenterPriceTypeLabel, systemConfigKeyEnum, truckRenterPriceTypeEnum } from '../../../../constants';
import { useUpdateDailyTrucksPriceBatchMutation } from '../../../../hooks/mutations/useDailyTruckMutation';
import { useTrucksCompaniesQuery } from '../../../../hooks/queries/useTrucksCompaniesQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailyTruck } from '../../../../services/apiSLTP/dailyReports/dailyTrucks';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyTruckPricesModalProps extends ModalProps {
    refetch: () => void;
    dailyTrucks: DailyTruck[];
    siteName?: string;
}

const ManageValidationDailyTruckPricesModal: React.FC<ManageValidationDailyTruckPricesModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyTrucks,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();

    const [selectedTruckCompany, setSelectedTruckCompany] = useState<OptionType | null>(null);

    const [unitPrice, setUnitPrice] = useState<string>('');
    const [priceType, setPriceType] = useState<OptionType | null>(null);

    const { data: dataTruckCompanies, isLoading: isTrucksCompaniesLoading } = useTrucksCompaniesQuery({
        queries: { active: true },
    });

    const { mutate: updateDailyTruckPrices, isLoading: isUpdatingPricesLoading } = useUpdateDailyTrucksPriceBatchMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const patchDailyTruckPrices = async () => {
        const dr = dailyTrucks.filter((truck) => truck.truckId.truckCompanyId._id === selectedTruckCompany?.value).map((truck) => truck._id);
        if (selectedTruckCompany?.value && dr.length > 0) {
            updateDailyTruckPrices({
                body: {
                    ids: dr,
                    unitPrice: unitPrice ? parseFloat(unitPrice) : null,
                    priceType: priceType?.value ? parseInt(priceType.value) : null,
                },
            });
        }
    };

    return (
        <ModalBody
            title={t('externalTrucks')}
            subtitle={t('updatePrices')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                patchDailyTruckPrices();
            }}
            disableSubmit={!selectedTruckCompany?.value}
            submitLoading={isUpdatingPricesLoading}
        >
            <DropDown
                selectedItem={selectedTruckCompany}
                items={
                    dataTruckCompanies
                        ?.filter((company) => dailyTrucks.some((truck) => truck.truckId.truckCompanyId._id === company._id && !company.isOwnCompany))
                        .map((company) => ({
                            label: company.designation,
                            value: company._id,
                        })) || []
                }
                handleChange={(event, value) => {
                    setSelectedTruckCompany(value);
                }}
                inputLabel={t('truckCompany')}
                required
                loading={isTrucksCompaniesLoading}
            />

            <>
                {!dataTruckCompanies?.find((company) => company._id === selectedTruckCompany?.value)?.isOwnCompany && (
                    <CustomTextInput
                        value={unitPrice}
                        onChange={setUnitPrice}
                        label={t('unitPrice')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                    />
                )}
                <DropDown
                    items={Object.values(truckRenterPriceTypeEnum).map((type) => ({
                        label: capitalizeFirstLetter(t(getTruckRenterPriceTypeLabel(type))),
                        value: type.toString(),
                    }))}
                    selectedItem={priceType}
                    handleChange={(e, value) => {
                        setPriceType(value);
                    }}
                    inputLabel={t('pricing')}
                    disableClearable={false}
                />
            </>
        </ModalBody>
    );
};

export default ManageValidationDailyTruckPricesModal;
